<template>
  <b-overlay :show="isLoading" rounded="sm">
    <template #overlay>
      <div class="text-center">
        <digger-loader />
      </div>
    </template>
    <transition name="bounce">
      <b-container class="py-5 custom-container container">
        <b-container class="custom-container mt-5">
          <b-breadcrumb
            :items="breadcrumbs"
            class="pl-0 pt-5 mt-5 bg-transparent"
          ></b-breadcrumb>

          <b-row>
            <b-col>
              <h2 class="font-weight-bold">
                {{ businessAdmin ? businessAdmin.full_name || '-----' : '-' }}
              </h2>
            </b-col>

            <b-col class="text-right align-right">
              <b-row>
                <b-col cols="12" sm="4" class="px-2"></b-col>
                <b-col cols="12" sm="4" class="px-2"></b-col>
                <b-col cols="12" sm="4" class="px-2">
                  <b-button
                    variant="outline-secondary"
                    class="bg-white text-dark"
                    :style="'max-height: 64px; max-width: 220px; width: 100%'"
                    @click="gotoBusinessAdmins()"
                  >
                    Back to Business Admins
                  </b-button>
                </b-col>
              </b-row>
            </b-col>
          </b-row>

          <b-row v-if="businessAdmin">
            <b-container fluid>
              <b-card class="mt-4 file-details">
                <div class="d-flex align-items-start mt-4" style="gap: 32px">
                  <!-- Business Admin Avatar / Upload -->
                  <image-cropper
                    v-if="businessAdmin.is_primary"
                    v-model="showImageCropper"
                    @change="uploadProfilePhoto"
                    @close="showImageCropper = false"
                    subtitle="Upload Business Admin's Profile Photo"
                    loadingMessage="Uploading Image"
                  />
                  <div
                    class="upload-container mb-4"
                    :class="avatarUrl ? 'border-0' : ''"
                    v-cloak
                    @drop.prevent="
                      businessAdmin.is_primary && onFileChange($event)
                    "
                    @dragover.prevent
                  >
                    <b-avatar
                      :src="avatarUrl"
                      :button="businessAdmin.is_primary"
                      @click="
                        businessAdmin.is_primary && (showImageCropper = true)
                      "
                      class="ml-0 mr-auto"
                      style="width: 180px; height: 240px"
                      rounded
                      :badge="businessAdmin.is_primary"
                      badge-top
                      badge-offset="0.5rem"
                      badge-variant="transparent"
                    >
                      <template v-if="businessAdmin.is_primary" #badge>
                        <b-icon
                          icon="pencil-square"
                          scale="1.5"
                          class="text-light"
                        />
                      </template>
                    </b-avatar>
                  </div>

                  <b-row no-gutters>
                    <b-col cols="12" class="mb-2">
                      <h4 class="mb-0">Profile</h4>
                      <small class="text-muted">
                        Overview of this business administrator
                      </small>
                    </b-col>

                    <b-col cols="12" md="6" lg="4" class="mb-3">
                      <div class="font-weight-bold">Name</div>
                      <div>{{ businessAdmin.full_name || '-' }}</div>
                    </b-col>

                    <b-col cols="12" md="6" lg="4" class="mb-3">
                      <div class="font-weight-bold">Email</div>
                      <div>{{ businessAdmin.email || '-' }}</div>
                    </b-col>

                    <b-col cols="12" md="6" lg="4" class="mb-3">
                      <div class="font-weight-bold">Phone</div>
                      <div>
                        {{
                          businessAdmin.phone_number
                            ? formatPhoneWithDialCode(
                                businessAdmin.phone_number,
                              )
                            : '-'
                        }}
                      </div>
                    </b-col>

                    <b-col cols="12" md="6" lg="4" class="mb-3">
                      <div class="font-weight-bold">Role</div>
                      <div>{{ roleDisplayName }}</div>
                    </b-col>

                    <b-col cols="12" md="6" lg="4" class="mb-3">
                      <div class="font-weight-bold">Primary Admin</div>
                      <div>{{ businessAdmin.is_primary ? 'Yes' : 'No' }}</div>
                    </b-col>

                    <b-col cols="12" md="6" lg="4" class="mb-3">
                      <div class="font-weight-bold">Token Assigned</div>
                      <div>{{ businessAdmin.has_token ? 'Yes' : 'No' }}</div>
                    </b-col>

                    <b-col
                      v-if="businessAdmin.has_token"
                      cols="12"
                      md="6"
                      lg="4"
                      class="mb-3"
                    >
                      <div class="font-weight-bold">Token Used At</div>
                      <div>{{ formatDate(businessAdmin.token_used_at) }}</div>
                    </b-col>

                    <b-col
                      v-if="businessAdmin.has_token"
                      cols="12"
                      md="6"
                      lg="4"
                      class="mb-3"
                    >
                      <div class="font-weight-bold">Token Expires At</div>
                      <div
                        :class="{
                          'text-danger font-weight-bold': isExpiringSoon(
                            businessAdmin.token_expires_at,
                            30,
                          ),
                          'text-warning':
                            isExpiringSoon(
                              businessAdmin.token_expires_at,
                              60,
                            ) &&
                            !isExpiringSoon(businessAdmin.token_expires_at, 30),
                        }"
                      >
                        {{ formatDate(businessAdmin.token_expires_at) }}
                      </div>
                    </b-col>
                  </b-row>
                </div>
              </b-card>

              <!-- Role management (not available for the primary admin) -->
              <b-card
                v-if="!businessAdmin.is_primary"
                class="mt-4 file-details"
              >
                <b-row no-gutters>
                  <b-col
                    cols="12"
                    class="mb-3 d-flex justify-content-between align-items-start"
                  >
                    <div>
                      <h4 class="mb-0">Role</h4>
                      <small class="text-muted">
                        Change what this Business Admin can access.
                      </small>
                    </div>
                    <b-button
                      v-if="canApplyRoles"
                      variant="primary"
                      :disabled="savingRole || !roleDirty"
                      @click="saveRole"
                    >
                      <b-spinner v-if="savingRole" small class="mr-1" />
                      Save
                    </b-button>
                  </b-col>
                  <b-col cols="12" md="8" lg="6">
                    <b-form-select
                      v-model="selectedRoleId"
                      :options="roleOptions"
                      :disabled="loadingRoles || !canApplyRoles"
                    />
                    <small
                      v-if="selectedRole && selectedRole.description"
                      class="text-muted d-block mt-1"
                    >
                      {{ selectedRole.description }}
                    </small>
                  </b-col>
                </b-row>
              </b-card>

              <!-- Project Access (project-scoped roles only) -->
              <b-card v-if="showProjectAccess" class="mt-4 file-details">
                <b-row no-gutters>
                  <b-col cols="12">
                    <h4 class="mb-0">Project Access</h4>
                    <small class="text-muted d-block mb-2">
                      This user can access the projects assigned to their role.
                    </small>
                    <b-alert show variant="info" class="py-2 mb-0">
                      Projects are managed on the role itself. Go to
                      <strong>My Account → Roles</strong> and edit
                      <strong>{{ roleDisplayName }}</strong> to change which
                      projects everyone with this role can access.
                    </b-alert>
                  </b-col>
                </b-row>
              </b-card>
            </b-container>
          </b-row>
        </b-container>
      </b-container>
    </transition>
  </b-overlay>
</template>

<script>
import { mapGetters } from 'vuex'
import ImageCropper from '@/components/modals/ImageCropper'
import { formatPhoneWithDialCode } from '@/utils/phonePolicy'

export default {
  components: {
    ImageCropper,
  },

  data() {
    return {
      businessAdminId: this.$route.params.businessAdminId,
      businessAdmin: null,
      isLoading: false,
      showImageCropper: false,

      // Role editor
      roles: [],
      loadingRoles: false,
      selectedRoleId: null,
      savedRoleId: null,
      savingRole: false,
    }
  },

  computed: {
    ...mapGetters('auth', ['hasPermission']),

    canApplyRoles() {
      return this.hasPermission('settings.roles.apply')
    },

    /** Human-readable role for the profile grid. */
    roleDisplayName() {
      if (!this.businessAdmin) return '-'
      if (this.businessAdmin.role_name) return this.businessAdmin.role_name
      return this.businessAdmin.role === 'supervisor' ? 'Supervisor' : 'Admin'
    },

    roleOptions() {
      return this.roles.map((r) => ({ value: r.id, text: r.name }))
    },

    selectedRole() {
      return this.roles.find((r) => r.id === this.selectedRoleId) || null
    },

    /**
     * Whether the currently-selected role is project-scoped. Falls back to the
     * BA's saved `is_project_scoped` (or legacy supervisor role) until the roles
     * list has loaded.
     */
    isProjectScopedRole() {
      if (this.selectedRole) return !!this.selectedRole.is_project_scoped
      if (!this.businessAdmin) return false
      if (typeof this.businessAdmin.is_project_scoped === 'boolean') {
        return this.businessAdmin.is_project_scoped
      }
      return this.businessAdmin.role === 'supervisor'
    },

    showProjectAccess() {
      return !!this.businessAdmin && this.isProjectScopedRole
    },

    roleDirty() {
      return (
        this.selectedRoleId != null && this.selectedRoleId !== this.savedRoleId
      )
    },

    breadcrumbs() {
      return [
        {
          text: 'Business Admins',
          to: { name: 'user.settings-page', hash: '#business-admins' },
        },
        {
          text: this.businessAdmin ? this.businessAdmin.full_name : '',
          active: true,
        },
      ]
    },

    avatarUrl() {
      if (!this.businessAdmin) return null
      const avatar = this.businessAdmin.avatar
      if (!avatar) return null
      return avatar.thumb_url || avatar.url || null
    },
  },

  methods: {
    formatPhoneWithDialCode,

    gotoBusinessAdmins() {
      this.$router.push({
        name: 'user.settings-page',
        hash: '#business-admins',
      })
    },

    async loadBusinessAdmin() {
      this.isLoading = true
      try {
        const { data } = await this.$api.get(
          `company/business-admins/${this.businessAdminId}`,
        )
        const raw = data.data || data
        this.businessAdmin = this.normaliseBusinessAdmin(raw)
        // Seed the role editor.
        this.savedRoleId = this.businessAdmin.role_id
        this.selectedRoleId = this.businessAdmin.role_id
      } catch (error) {
        console.error(error)
      } finally {
        this.isLoading = false
      }
    },

    normaliseBusinessAdmin(raw) {
      const user = raw.user || raw
      const token = raw.token || null

      return {
        id: raw.user_id || (user && user.id),
        full_name: (user && user.full_name) || raw.full_name,
        email: (user && user.email) || raw.email,
        phone_number: (user && user.phone_number) || raw.phone_number,
        is_primary: !!(raw.is_primary != null
          ? raw.is_primary
          : user && user.is_primary),
        has_token: !!(raw.has_token != null
          ? raw.has_token
          : user && user.has_token),
        is_archived: !!(raw.is_archived != null
          ? raw.is_archived
          : user && user.is_archived),
        role: raw.role || (user && user.role) || 'admin',
        role_id:
          raw.role_id != null
            ? raw.role_id
            : user && user.role_id != null
            ? user.role_id
            : null,
        role_name: raw.role_name || (user && user.role_name) || null,
        is_project_scoped:
          raw.is_project_scoped != null
            ? raw.is_project_scoped
            : user && user.is_project_scoped != null
            ? user.is_project_scoped
            : null,
        assigned_project_ids: this.extractAssignedProjectIds(raw, user),
        avatar: raw.avatar || (user && user.avatar) || null,
        token_used_at: (token && token.used_at) || raw.token_used_at || null,
        token_expires_at:
          (token && token.expires_at) ||
          raw.token_expires_at ||
          raw.expires_at ||
          null,
      }
    },

    /**
     * Pulls assigned project ids out of the BA payload, tolerating either
     * `assigned_project_ids: [1,2]` or `assigned_projects: [{id:1},...]`.
     */
    extractAssignedProjectIds(raw, user) {
      const source =
        raw.assigned_project_ids ||
        (user && user.assigned_project_ids) ||
        raw.assigned_projects ||
        (user && user.assigned_projects) ||
        []
      if (!Array.isArray(source)) return []
      return source
        .map((p) => (p && typeof p === 'object' ? p.id : p))
        .filter((id) => id != null)
    },

    async loadRoles() {
      this.loadingRoles = true
      try {
        const { data } = await this.$api.get('company/roles')
        const list = (data && data.data) || data
        this.roles = Array.isArray(list) ? list : []
      } catch (e) {
        this.roles = []
      } finally {
        this.loadingRoles = false
      }
    },

    async saveRole() {
      if (!this.businessAdmin || !this.roleDirty) return
      this.savingRole = true
      try {
        await this.$api.put(
          `company/business-admins/${this.businessAdminId}/role`,
          { role_id: this.selectedRoleId },
        )
        this.savedRoleId = this.selectedRoleId
        // Reflect the new role in the profile grid + project-access visibility.
        if (this.selectedRole) {
          this.businessAdmin.role_name = this.selectedRole.name
          this.businessAdmin.is_project_scoped =
            !!this.selectedRole.is_project_scoped
        }
        this.$bvToast.toast('Role updated.', {
          title: 'Success',
          variant: 'success',
          autoHideDelay: 3000,
        })
      } catch (error) {
        const status = error.response && error.response.status
        const apiMessage =
          error.response && error.response.data && error.response.data.message
        const message =
          apiMessage ||
          (status === 403
            ? "You don't have permission to change roles."
            : 'Failed to update role.')
        this.$bvToast.toast(message, {
          title: 'Oops.. Something went wrong!',
          variant: 'danger',
          autoHideDelay: 5000,
        })
      } finally {
        this.savingRole = false
      }
    },

    formatDate(dateString) {
      if (!dateString) return '-'
      return new Date(dateString).toLocaleDateString('en-AU', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
    },

    isExpiringSoon(dateString, days) {
      if (!dateString) return false
      const diff = new Date(dateString) - new Date()
      return diff > 0 && diff < days * 24 * 60 * 60 * 1000
    },

    onFileChange(e) {
      this.showImageCropper = true
      this.$root.$emit('fileChange', e)
    },

    async uploadProfilePhoto(image) {
      if (!this.businessAdmin) return

      const data = new FormData()
      data.append('avatar', image)
      data.append('user_id', this.businessAdmin.id)

      try {
        await this.$api.post('auth/profile/avatar', data)
        this.showImageCropper = false
        await this.loadBusinessAdmin()
      } catch (error) {
        console.error(error)
      } finally {
        this.$root.$emit('uploadComplete')
      }
    },
  },

  mounted() {
    this.loadBusinessAdmin()
    this.loadRoles()
  },
}
</script>

<style></style>
